(() => {
  'use strict';

  const ROOT_SELECTOR =
    '[data-nonstaff-cart-cross-sell]';

  const TOP_SIZES = [
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL',
    '4XL',
    '5XL'
  ];

  const normalize = (value) => {
    return String(value ?? '')
      .normalize('NFKC')
      .trim()
      .toUpperCase();
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const parseJson = (
    element,
    fallback = []
  ) => {
    if (!element) return fallback;

    try {
      return JSON.parse(
        element.textContent
      );
    } catch (error) {
      console.error(
        'Non-Staff cross-sell JSON error:',
        error
      );

      return fallback;
    }
  };

  const formatMoney = (
    priceInCents
  ) => {
    return new Intl.NumberFormat(
      'ja-JP',
      {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0
      }
    ).format(
      Number(priceInCents) / 100
    );
  };

  const detectGender = (product) => {
    const values = [
      ...(product.tags || []),
      product.title
    ].map(normalize);

    if (
      values.some((value) =>
        value.includes('レディース')
      )
    ) {
      return 'women';
    }

    if (
      values.some((value) =>
        value.includes('メンズ')
      )
    ) {
      return 'men';
    }

    return null;
  };

  const isPantsProduct = (
    product
  ) => {
    return (
      product.tags || []
    ).some((tag) => {
      return normalize(tag) === 'パンツ';
    });
  };

  const extractTopSize = (
    product
  ) => {
    const sizePattern =
      /(5XL|4XL|XXXL|XXL|XL|XXS|XS|L|M|S)/;

    const wearingTag = (
      product.tags || []
    ).find((tag) => {
      return normalize(tag)
        .includes('着用感');
    });

    if (wearingTag) {
      const match =
        normalize(wearingTag)
          .match(sizePattern);

      if (match) {
        return match[1];
      }
    }

    const titleMatch =
      normalize(product.title).match(
        /\[(?:メンズ|レディース)?(5XL|4XL|XXXL|XXL|XL|XXS|XS|L|M|S)\]/
      );

    if (titleMatch) {
      return titleMatch[1];
    }

    const displayTag = (
      product.tags || []
    ).find((tag) => {
      return normalize(tag)
        .includes('表記サイズ');
    });

    if (displayTag) {
      const match =
        normalize(displayTag)
          .match(sizePattern);

      if (match) {
        return match[1];
      }
    }

    return null;
  };

  const extractWaist = (
    product
  ) => {
    const tags =
      product.tags || [];

    const actualTag =
      tags.find((tag) => {
        return /^実寸W[0-9]{2}相当$/i
          .test(normalize(tag));
      });

    if (actualTag) {
      const match =
        normalize(actualTag)
          .match(/W([0-9]{2})/);

      if (match) {
        return Number(match[1]);
      }
    }

    const waistTag =
      tags.find((tag) => {
        return /^W[0-9]{2}$/i
          .test(normalize(tag));
      });

    if (waistTag) {
      const match =
        normalize(waistTag)
          .match(/W([0-9]{2})/);

      if (match) {
        return Number(match[1]);
      }
    }

    const titleMatch =
      normalize(product.title).match(
        /\[(?:メンズ|レディース)?W([0-9]{2})\]/
      );

    if (titleMatch) {
      return Number(
        titleMatch[1]
      );
    }

    return null;
  };

  const isGenderCompatible = (
    product,
    profile
  ) => {
    const productGender =
      detectGender(product);

    if (
      !profile.gender ||
      !productGender
    ) {
      return true;
    }

    return (
      profile.gender ===
      productGender
    );
  };

  const getSizeMatch = (
    product,
    profile
  ) => {
    if (
      !isGenderCompatible(
        product,
        profile
      )
    ) {
      return null;
    }

    if (
      isPantsProduct(product)
    ) {
      if (
        !profile.pants?.value
      ) {
        return null;
      }

      const productWaist =
        extractWaist(product);

      if (!productWaist) {
        return null;
      }

      const difference =
        Math.abs(
          Number(
            profile.pants.value
          ) - productWaist
        );

      if (difference > 2) {
        return null;
      }

      return {
        type: 'pants',
        difference,
        label: `W${productWaist}`
      };
    }

    if (!profile.top?.value) {
      return null;
    }

    const productSize =
      extractTopSize(product);

    if (!productSize) {
      return null;
    }

    const profileRank =
      TOP_SIZES.indexOf(
        profile.top.value
      );

    const productRank =
      TOP_SIZES.indexOf(
        productSize
      );

    if (
      profileRank === -1 ||
      productRank === -1
    ) {
      return null;
    }

    const difference =
      Math.abs(
        profileRank -
        productRank
      );

    if (difference > 1) {
      return null;
    }

    return {
      type: 'top',
      difference,
      label: productSize
    };
  };

  const buildRequestUrl = (
    baseUrl,
    page
  ) => {
    const url = new URL(
      baseUrl,
      window.location.origin
    );

    url.searchParams.set(
      'page',
      String(page)
    );

    const previewThemeId =
      new URLSearchParams(
        window.location.search
      ).get('preview_theme_id');

    if (previewThemeId) {
      url.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    return url.toString();
  };

  const fetchAllProducts = async (
    baseUrl
  ) => {
    const products = [];

    let page = 1;
    let requestCount = 0;

    while (
      page &&
      requestCount < 5
    ) {
      const response =
        await fetch(
          buildRequestUrl(
            baseUrl,
            page
          ),
          {
            method: 'GET',
            credentials:
              'same-origin',
            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          `商品取得に失敗しました: ${response.status}`
        );
      }

      const data =
        await response.json();

      products.push(
        ...(data.products || [])
      );

      page =
        data.nextPage || null;

      requestCount += 1;
    }

    return products;
  };

  const selectProducts = ({
    products,
    profile,
    cartProductIds,
    remainingAmount
  }) => {
    const uniqueProducts =
      new Map();

    products.forEach(
      (product) => {
        const productId =
          Number(product.id);

        if (
          cartProductIds.includes(
            productId
          )
        ) {
          return;
        }

        if (!product.available) {
          return;
        }

        if (
          Number(product.price) <
          remainingAmount
        ) {
          return;
        }

        const sizeMatch =
          getSizeMatch(
            product,
            profile
          );

        if (!sizeMatch) {
          return;
        }

        uniqueProducts.set(
          productId,
          {
            ...product,
            sizeMatch,
            priceDifference:
              Number(product.price) -
              remainingAmount
          }
        );
      }
    );

    return Array.from(
      uniqueProducts.values()
    )
.sort(
  (productA, productB) => {
    // 1. サイズの合い方を最優先
    if (
      productA.sizeMatch.difference !==
      productB.sizeMatch.difference
    ) {
      return (
        productA.sizeMatch.difference -
        productB.sizeMatch.difference
      );
    }

    // 2. 送料無料未達なら、
    // 必要金額に近い商品を優先
    if (remainingAmount > 0) {
      if (
        productA.priceDifference !==
        productB.priceDifference
      ) {
        return (
          productA.priceDifference -
          productB.priceDifference
        );
      }
    }

    // 3. 送料無料達成後は
    // 買い足しやすい低価格順
    return (
      Number(productA.price) -
      Number(productB.price)
    );
  }
)
.slice(0, 10);
  };

  const createProductCard = (
    product
  ) => {
    const imageHtml =
      product.image
        ? `
          <img
            src="${escapeHtml(product.image)}"
            alt="${escapeHtml(product.title)}"
            width="240"
            height="300"
            loading="lazy"
          >
        `
        : `
          <div
            class="nonstaff-cross-sell-card__placeholder"
            aria-hidden="true"
          ></div>
        `;

    return `
      <article
        class="nonstaff-cross-sell-card"
      >
        <a
          class="nonstaff-cross-sell-card__link"
          href="${escapeHtml(product.url)}"
        >
          <div
            class="nonstaff-cross-sell-card__media"
          >
            ${imageHtml}

            <span
              class="nonstaff-cross-sell-card__size"
            >
              ${escapeHtml(
                product.sizeMatch.label
              )}
            </span>
          </div>

          <p
            class="nonstaff-cross-sell-card__price"
          >
            ${escapeHtml(
              formatMoney(
                product.price
              )
            )}
          </p>

          <h3
            class="nonstaff-cross-sell-card__title"
          >
            ${escapeHtml(
              product.title
            )}
          </h3>
        </a>

        <div class="nonstaff-cross-sell-card__actions">
  <button
    type="button"
    class="nonstaff-cross-sell-card__button"
    data-nonstaff-cross-sell-add
    data-variant-id="${escapeHtml(
      product.variantId
    )}"
  >
    ＋ カートに追加
  </button>

  <a
    class="nonstaff-cross-sell-card__details"
    href="${escapeHtml(product.url)}"
  >
    商品を見る
  </a>
</div>
      </article>
    `;
  };

  const addProductToCart = async (
    button
  ) => {
    if (button.disabled) {
      return;
    }

    const variantId =
      Number(
        button.dataset.variantId
      );

    if (!variantId) {
      return;
    }

    const originalText =
      button.textContent;

    button.disabled = true;

    button.classList.add(
      'is-loading'
    );

    button.textContent =
      '追加中…';

    try {
      const cartAddUrl =
        window.routes
          ?.cart_add_url ||
        '/cart/add.js';

      const response =
        await fetch(
          cartAddUrl,
          {
            method: 'POST',
            credentials:
              'same-origin',
            headers: {
              'Content-Type':
                'application/json',
              Accept:
                'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity: 1
                }
              ]
            })
          }
        );

      if (!response.ok) {
        const errorData =
          await response
            .json()
            .catch(() => ({}));

        throw new Error(
          errorData.description ||
          '商品を追加できませんでした。'
        );
      }

      button.textContent =
  '✓ 追加しました';

button.classList.remove(
  'is-loading'
);

const isCartPage =
  window.location.pathname
    .replace(/\/$/, '')
    .endsWith('/cart');

if (isCartPage) {
  window.location.reload();
  return;
}

document.dispatchEvent(
  new CustomEvent(
    'nonstaff:cross-sell-added',
    {
      detail: {
        variantId: variantId
      }
    }
  )
);

await initialize();
    } catch (error) {
      console.error(
        'Non-Staff cart add error:',
        error
      );

      button.classList.remove(
        'is-loading'
      );

      button.textContent =
        '追加できませんでした';

      window.setTimeout(
        () => {
          button.disabled = false;

          button.textContent =
            originalText;
        },
        1800
      );
    }
  };

  const waitForSizeProfile =
    async () => {
      for (
        let attempt = 0;
        attempt < 60;
        attempt += 1
      ) {
        if (
          window
            .NonstaffCartSizeProfile
        ) {
          return window
            .NonstaffCartSizeProfile;
        }

        await new Promise(
          (resolve) => {
            window.setTimeout(
              resolve,
              50
            );
          }
        );
      }

      return null;
    };

const initialize = async () => {
  const root =
    document.querySelector(
      ROOT_SELECTOR
    );

  if (!root) {
    return;
  }

  const loading =
    root.querySelector(
      '[data-nonstaff-cross-sell-loading]'
    );

  const productContainer =
    root.querySelector(
      '[data-nonstaff-cross-sell-products]'
    );

    const previousScrollLeft =
  productContainer.scrollLeft || 0;

  const emptyMessage =
    root.querySelector(
      '[data-nonstaff-cross-sell-empty]'
    );

  const eyebrow =
    root.querySelector(
      '.nonstaff-cart-cross-sell__eyebrow'
    );

  const titleElement =
    root.querySelector(
      '.nonstaff-cart-cross-sell__title'
    );

    const remainingElement =
  root.querySelector(
    '[data-nonstaff-cross-sell-remaining]'
  );

  if (
    !loading ||
    !productContainer ||
    !emptyMessage
  ) {
    return;
  }
  loading.hidden = false;

productContainer.hidden = true;
productContainer.innerHTML = '';

emptyMessage.hidden = true;

const threshold =
  Number(
    root.dataset
      .freeShippingThreshold || 0
  );

const shopRoot =
  window.Shopify?.routes?.root || '/';

const cartResponse =
  await fetch(
    shopRoot + 'cart.js',
    {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    }
  );

if (!cartResponse.ok) {
  throw new Error(
    '最新のカート情報を取得できませんでした。'
  );
}

const liveCart =
  await cartResponse.json();

const cartTotal =
  Number(liveCart.total_price || 0);

const remainingAmount =
  Math.max(
    threshold - cartTotal,
    0
  );

if (remainingAmount <= 0) {
  if (titleElement) {
    titleElement.textContent =
      '送料無料対象金額に達しました';
  }

  if (remainingElement) {
    remainingElement.hidden = true;
    remainingElement.textContent = '';
  }
} else {
  if (titleElement) {
    titleElement.textContent =
      'あと1点で送料無料になる商品';
  }

  if (remainingElement) {
    remainingElement.hidden = false;
    remainingElement.innerHTML =
      '送料無料まであと <strong>' +
      formatMoney(remainingAmount) +
      '</strong>';
  }
}

const cartProductIds =
  (liveCart.items || []).map(
    (item) => Number(item.product_id)
  );

  try {
    /*
     * サイズ情報は表示しないが、
     * 商品選定には引き続き使用する
     */
    const profile =
      await waitForSizeProfile();

    if (!profile) {
      throw new Error(
        'サイズ情報を取得できませんでした。'
      );
    }

    const products =
      await fetchAllProducts(
        root.dataset.collectionUrl
      );

    const selectedProducts =
      selectProducts({
        products,
        profile,
        cartProductIds,
        remainingAmount
      });

    loading.hidden = true;

    if (
      selectedProducts.length === 0
    ) {
      emptyMessage.textContent =
        '現在の条件に合う古着が見つかりませんでした。';

      emptyMessage.hidden = false;
      return;
    }

    productContainer.innerHTML =
      selectedProducts
        .map(createProductCard)
        .join('');

    productContainer.hidden =
      false;

      requestAnimationFrame(() => {
  productContainer.scrollLeft =
    previousScrollLeft;
});

if (
  productContainer.dataset
    .nonstaffAddBound !== 'true'
) {
  productContainer.dataset
    .nonstaffAddBound = 'true';

  productContainer.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          '[data-nonstaff-cross-sell-add]'
        );

      if (
        !button ||
        !productContainer.contains(
          button
        )
      ) {
        return;
      }

      addProductToCart(button);
    }
  );
}
  } catch (error) {
    console.error(
      'Non-Staff cart cross-sell:',
      error
    );

    loading.hidden = true;

    emptyMessage.textContent =
      'おすすめ商品を読み込めませんでした。';

    emptyMessage.hidden = false;
  }
};

if (
  !window
    .__nonstaffCartCrossSellInitialized
) {
  window
    .__nonstaffCartCrossSellInitialized =
    true;

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
}

document.addEventListener(
  'nonstaff:cart-added',
  function() {
    initialize();
  }
);

/*
 * Dawnのごみ箱・数量変更を検知します。
 *
 * Ajaxでカートが更新されたあとに
 * ページを再読み込みすることで、
 *
 * ・現在のカート合計
 * ・送料無料の達成状況
 * ・カート内の商品ID
 * ・購入者の推定サイズ
 * ・おすすめ商品
 *
 * をすべて最新状態にします。
 */
const bindCartUpdateReload = (
  attempt = 0
) => {
  /*
   * 同じイベントを二重登録しない
   */
  if (
    window
      .__nonstaffCartUpdateReloadBound
  ) {
    return;
  }

  /*
   * Dawnのカート更新イベントが
   * 利用可能になったら登録する
   */
  if (
    typeof subscribe ===
      'function' &&
    typeof PUB_SUB_EVENTS !==
      'undefined' &&
    PUB_SUB_EVENTS.cartUpdate
  ) {
    window
      .__nonstaffCartUpdateReloadBound =
      true;

    subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      () => {
        /*
         * 数量変更などでイベントが
         * 連続して発生しても、
         * 再読み込みは1回だけにする
         */
        window.clearTimeout(
          window
            .__nonstaffCartUpdateReloadTimer
        );

        window
          .__nonstaffCartUpdateReloadTimer =
          window.setTimeout(
            () => {
              window.location.reload();
            },
            300
          );
      }
    );

    return;
  }

  /*
   * pubsub.jsの読み込みが
   * まだ完了していない場合は再確認
   */
  if (attempt < 20) {
    window.setTimeout(
      () => {
        bindCartUpdateReload(
          attempt + 1
        );
      },
      100
    );
  }
};

bindCartUpdateReload();
})();